import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import {setEffectParameterValues} from 'actions/index.js';
import Effected from './Effected.js';
import PolyParameter from 'parameters/PolyParameter.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import Tone from 'tone';
import {AbstractParameter} from 'parameters/AbstractParameter.js';

var formantCount = 0;
var disposedFormantCount = 0;

export default class FormantNode extends Effected(class {}){
    constructor(formants, number) {
        super();
        this.initialBulkValues = {};
        this.formants = formants;
        this.oscillator = formants.oscillator;
        this.number = number;
        this.parameters = [];
        this.name = 'Formant ' + number;
        this.enabled = false;

        this.setup();
        this.sequencerEngine.dispatch(
            setEffectParameterValues(this.initialBulkValues));
    }

    get sequencerEngine() {
        return this.oscillator.sequencerEngine;
    }

    registerParameter(parameter, signalGetter) {
        this.initialBulkValues = {
            ...this.initialBulkValues,
            ... parameter.setStore(this.oscillator.store)
        };
        parameter.setMixSession(this.oscillator.mixSession);
        let context = this.oscillator.context.getCopy();
        context.forFormant(this);
        context.forSubDevice(this);
        this.context = context;
        parameter.setContext(context);
        parameter.setTrack(this.oscillator.context.track);
        this.parameters.push(parameter);
        this.oscillator.parameters.push(parameter);
    }

    get input() {
        return this.formants.input;
    }

    get output() {
        return this.formants.output;
    }

    initialize() {
        if (!this.formantFilter && !this.formantGain) {
            this.formantFilter = new Tone.Filter(
                this.formantFrequencyParameter.get(true), "bandpass", this.rolloff);
            this.formantGain = new Tone.Gain(1);
            this.setAudioParams();
        }
        this.input.chain(
            this.formantFilter,
            this.formantGain,
            this.output);
        this.enabled = true;
    }

    get isEnabled() {
        return this.enabledParameter.get();
    }

    bypass() {
        if (!this.enabled) {
            // its already  disabled
            return;
        }
        if (this.formantFilter && this.formantGain) {
            this.input.disconnect(this.formantFilter);
            this.formantFilter.disconnect(this.formantGain);
            this.formantGain.disconnect();
        }
        this.enabled = false;
    }

    setup(reload=false) {
        if (!reload) {
            this.enabledParameter = new AbstractParameter(
                "Formant " + (this.number + 1) + " Enabled",
                this,
                (o, x) => {
                    this.enable(x);
                },
                {
                    documentation: [
                        "When enabled, a formant filter is applied to the operator",
                    ],
                    min: 0,
                    max: 1,
                    defaultValue: false,
                    automatable: false,
                }
            );
            this.registerParameter(this.enabledParameter);
            
            this.formantFrequencyParameter = new PolyParameter(
                'Formant Frequency ' + (this.number + 1), undefined,
                {
                    min: 0,
                    max: 20000,
                    unit: '',
                    defaultValue: 500 * (this.number + 1),
                });

            this.registerParameter(this.formantFrequencyParameter);

            this.formantQParameter = new PolyParameter(
                'Formant Q ' + (this.number + 1), undefined,
                {
                    min: 0,
                    documentation: [
                        "Ratio between this operator`'s frequency, and the frequency of the inputed note.",
                        "Ex: A ratio of 2 is an octave above the inputed note"],
                    max: 80,
                    unit: '',
                    defaultValue: 20,
                });
            this.registerParameter(this.formantQParameter);

            this.formantGainParameter = new PolyParameter(
                'Formant Gain ' + (this.number + 1), undefined,
                {
                    min: 0,
                    documentation: [
                        "Ratio between this operator`'s frequency, and the frequency of the inputed note.",
                        "Ex: A ratio of 2 is an octave above the inputed note"],
                    max: 4,
                    unit: '',
                    defaultValue: 0.5,
                });
            this.registerParameter(this.formantGainParameter);

            this.formantRolloffParameter = new AbstractParameter(
                'Formant Rolloff ' + (this.number + 1),
                this, (a,b) => this.rolloff = b,
                {
                    defaultValue: -12,
                    min: -48,
                    max: -12,
                    automatable: false
                });
            this.registerParameter(this.formantRolloffParameter);
        }
    }

    set rolloff(x) {
        if (this.formantFilter) {
            this.formantFilter.rolloff = x;
        }
    }

    get rolloff() {
        return this.formantRolloffParameter ? this.formantRolloffParameter.get() : -12;
    }

    enable(enable) {
        if (enable && !this.formants.anyFormantsOn()) {
            // this is the first formant to be turned on
            // so we need to enable formants on the whole
            this.formants.enableFormants();
        } else if (this.formants.numFormants() === 1 && !enable && this.enabled) {
            // then we are turning off the last formant
            this.formants.disableFormants();
        }

        if (enable) {
            this.initialize();
        } else {
            this.bypass();
        }
    }

    refreshParameters() {
        // refresh the gain parameter
        this.formantFrequencyParameter.set(this.formantFrequencyParameter.get(true));
        this.formantGainParameter.set(this.formantGainParameter.get(true));
        this.formantQParameter.set(this.formantQParameter.get(true));
    }

    setAudioParams() {
        this.formantQParameter.setAudioParams([this.formantFilter.Q]);
        this.formantFrequencyParameter.setAudioParams([this.formantFilter.frequency]);
        this.formantGainParameter.setAudioParams([this.formantGain.gain]);
    }

    removeAudioParams(formantFilter, formantGain) {
        this.formantQParameter.removeAudioParams([this.formantFilter.Q]);
        this.formantFrequencyParameter.removeAudioParams([this.formantFilter.frequency]);
        this.formantGainParameter.removeAudioParams([this.formantGain.gain]);
        this.formantQParameter.disableKeyScaling();
        this.formantFrequencyParameter.disableKeyScaling();
        this.formantGainParameter.disableKeyScaling();
    }

    getJson(isPreset) {
        let json = {
            enabled: this.enabledParameter.get(true),
            frequency: this.formantFrequencyParameter.get(true),
            q: this.formantQParameter.get(true),
            gain: this.formantGainParameter.get(true),
            effect: this.effect && this.effect.getJson(),
            rolloff: this.rolloff
        };

        json = saveParameterToJson(json, 'enabledParameter', this.enabledParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'formantFrequencyParameter', this.formantFrequencyParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'formantQParameter', this.formantQParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'formantGainParameter', this.formantGainParameter, undefined, isPreset);
        return json;
    }

    
    fromJson(json, ignoreId) {
        let bulk = {};
        if (json.rolloff) {
            bulkUpdate(this.formantRolloffParameter, json.rolloff, bulk);
        }
        if (this.formantFilter) {
            this.formantFilter.rolloff = this.rolloff;
        }
        bulkUpdate(this.enabledParameter, json.enabled, bulk);
        if (!this.enabledParameter.get(true)) {
            this.bypassEffect();
            return;
        }
        bulkUpdate(this.formantFrequencyParameter, json.frequency, bulk);
        bulkUpdate(this.formantQParameter, json.q, bulk);
        bulkUpdate(this.formantGainParameter, json.gain, bulk);

        let a = new Date().getTime();
        if (json.effect) {
            this.loadEffectFromJson(json.effect);
        } else {
            this.bypassEffect();
        }
        let b = new Date().getTime();

        bulk = {
            ... bulk,
            //... loadParameterFromJson(
            //    json, 'enabledParameter', this.enabledParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'formantFrequencyParameter', this.formantFrequencyParameter, this.sequencerEngine, ignoreId, true)
        };
        bulk = {
            ... bulk,
            ...loadParameterFromJson(
                json, 'formantQParameter', this.formantQParameter, this.sequencerEngine, ignoreId, true)
        };
        bulk ={
            ... bulk,
            ...loadParameterFromJson(
                json, 'formantGainParameter', this.formantGainParameter, this.sequencerEngine, ignoreId, true),
        };

        return bulk;
    }

    disconnectParameters() {
    }

    ripMods() {
    }

    isConnectedToAnyModulator() {
        return this.formantGainParameter.isConnectedToAnyModulator() ||
            this.formantFrequencyParameter.isConnectedToAnyModulator() ||
            this.formantQParameter.isConnectedToAnyModulator();
    }

    dispose() {
        if (this.formantFilter && this.formantGain) {
            this.parameters.forEach(
                p => p.dispose(true));
        }
    }

    getParameters() {
        let p = [... this.parameters];
        if (this.effect) {
            p = [... p, ... this.effect.getParameters()];
        }
        return p;
    }
}

